import React, { useState, useRef } from 'react';
import axios from 'axios';
import { PawPrint, Plus, Trash2, Loader2, ArrowRight, Calendar, Camera, X as XIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ageFromBirthday } from '../utils/age';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyDog = () => ({ dog_name: '', breed: '', birthday: '', photo: null, photoPreview: '' });

/**
 * Form to register up to `maxDogs` dogs for the currently logged-in customer.
 * Calls POST /api/account/dog-profiles for each filled row.
 *
 * Props:
 *   maxDogs:    number — hard limit on rows (default 2)
 *   onDone:     () => void — fired when the user finishes (saved or skipped)
 *   onSkip:     () => void — fired when the user clicks Skip (defaults to onDone)
 *   skipLabel:  string — custom label for the skip button
 */
export const DogRegistrationStep = ({
  maxDogs = 2,
  onDone,
  onSkip,
  skipLabel = 'Skip for now',
}) => {
  const [dogs, setDogs] = useState([emptyDog()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRefs = useRef({});

  const updateDog = (index, field, value) => {
    setDogs(prev => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  const handlePhotoPick = (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5 MB)');
      return;
    }
    const preview = URL.createObjectURL(file);
    setDogs(prev => prev.map((d, i) => (i === index ? { ...d, photo: file, photoPreview: preview } : d)));
  };

  const clearPhoto = (index) => {
    setDogs(prev => prev.map((d, i) => {
      if (i !== index) return d;
      if (d.photoPreview) URL.revokeObjectURL(d.photoPreview);
      return { ...d, photo: null, photoPreview: '' };
    }));
  };

  const addRow = () => {
    if (dogs.length >= maxDogs) return;
    setDogs(prev => [...prev, emptyDog()]);
  };

  const removeRow = (index) => {
    setDogs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setError('');
    // Keep only rows with a name — those are the real submissions
    const toSave = dogs
      .map(d => ({
        dog_name: (d.dog_name || '').trim(),
        breed: (d.breed || '').trim(),
        birthday: (d.birthday || '').trim(),
        photo: d.photo,
      }))
      .filter(d => d.dog_name.length > 0);

    if (toSave.length === 0) {
      setError('Please add at least one dog, or click "Skip for now" below.');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('customer_token');
      const headers = { Authorization: `Bearer ${token}` };
      for (const dog of toSave) {
        // 1. Create the dog profile
        const { data: created } = await axios.post(
          `${API}/account/dog-profiles`,
          {
            dog_name: dog.dog_name,
            breed: dog.breed || null,
            birthday: dog.birthday || null,
            age: dog.birthday ? ageFromBirthday(dog.birthday) : null,
          },
          { headers },
        );
        // 2. If a photo was staged, attach it. If this call fails we still
        // keep the dog — just surface a soft warning instead of blocking.
        if (dog.photo && created?.id) {
          try {
            const form = new FormData();
            form.append('file', dog.photo);
            await axios.post(
              `${API}/account/dog-profiles/${created.id}/avatar`,
              form,
              { headers },
            );
          } catch {
            toast.warning(`Couldn't upload photo for ${dog.dog_name} — add it later from your account.`);
          }
        }
      }
      toast.success(toSave.length === 1 ? `Saved ${toSave[0].dog_name}!` : `Saved ${toSave.length} dogs!`);
      onDone && onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : err.message || 'Could not save dogs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dog-registration-step">
      <div className="text-left">
        <h3 className="text-lg font-black text-[#1a1a1a] mb-1 flex items-center gap-2">
          <PawPrint className="w-5 h-5 text-[#0FA49C]" /> Tell us about your dog{maxDogs > 1 ? '(s)' : ''}
        </h3>
        <p className="text-xs text-gray-600">
          Optional — but it helps Ted recommend the right food, remember birthdays and personalise events. You can edit this any time from your account.
        </p>
      </div>

      {error && (
        <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-xl p-3" data-testid="dog-step-error">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {dogs.map((dog, i) => {
          const ageHint = ageFromBirthday(dog.birthday);
          return (
            <div
              key={i}
              className="rounded-xl border-2 border-gray-200 p-3 bg-gray-50/50 relative"
              data-testid={`dog-row-${i}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#1f6d62]">
                  <PawPrint className="w-3 h-3" /> Dog {i + 1}
                </span>
                {dogs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1"
                    data-testid={`dog-row-${i}-remove`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>

              <div className="space-y-2.5">
                {/* Photo picker — optional */}
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    {dog.photoPreview ? (
                      <img
                        src={dog.photoPreview}
                        alt="preview"
                        className="w-14 h-14 rounded-full object-cover border-2 border-[#0FA49C] shadow-sm"
                        data-testid={`dog-row-${i}-photo-preview`}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                        <PawPrint className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRefs.current[i]?.click()}
                      disabled={saving}
                      className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-[#1f6d62] hover:bg-[#174f46] text-white shadow flex items-center justify-center transition-colors disabled:opacity-60"
                      title={dog.photoPreview ? 'Change photo' : 'Add a photo'}
                      data-testid={`dog-row-${i}-photo-btn`}
                    >
                      <Camera className="w-3 h-3" />
                    </button>
                    {dog.photoPreview && !saving && (
                      <button
                        type="button"
                        onClick={() => clearPhoto(i)}
                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white shadow flex items-center justify-center transition-colors"
                        title="Remove photo"
                        data-testid={`dog-row-${i}-photo-clear`}
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    )}
                    <input
                      ref={(el) => { fileInputRefs.current[i] = el; }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => handlePhotoPick(i, e)}
                      className="hidden"
                      data-testid={`dog-row-${i}-photo-input`}
                    />
                  </div>
                  <div className="text-xs text-gray-500 leading-snug">
                    <span className="font-semibold text-gray-700 block">Photo <span className="font-normal text-gray-400">(optional)</span></span>
                    Tap the camera to add a picture of your dog.
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">Dog&apos;s name</label>
                  <Input
                    type="text"
                    value={dog.dog_name}
                    onChange={(e) => updateDog(i, 'dog_name', e.target.value)}
                    placeholder="e.g. Ted"
                    className="h-10 mt-1 bg-white"
                    disabled={saving}
                    autoComplete="off"
                    data-testid={`dog-row-${i}-name`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wide">Breed</label>
                    <Input
                      type="text"
                      value={dog.breed}
                      onChange={(e) => updateDog(i, 'breed', e.target.value)}
                      placeholder="e.g. Cockapoo"
                      className="h-10 mt-1 bg-white"
                      disabled={saving}
                      autoComplete="off"
                      data-testid={`dog-row-${i}-breed`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Birthday
                    </label>
                    <Input
                      type="date"
                      value={dog.birthday}
                      onChange={(e) => updateDog(i, 'birthday', e.target.value)}
                      max={new Date().toISOString().slice(0, 10)}
                      className="h-10 mt-1 bg-white"
                      disabled={saving}
                      data-testid={`dog-row-${i}-birthday`}
                    />
                  </div>
                </div>

                {ageHint && (
                  <p className="text-xs text-[#0FA49C] font-semibold" data-testid={`dog-row-${i}-age`}>
                    Age: {ageHint}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dogs.length < maxDogs && (
        <button
          type="button"
          onClick={addRow}
          disabled={saving}
          className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-sm font-bold text-[#1f6d62] hover:border-[#0FA49C] hover:bg-[#0FA49C]/5 transition-all inline-flex items-center justify-center gap-2"
          data-testid="dog-step-add-another"
        >
          <Plus className="w-4 h-4" /> Add another dog
        </button>
      )}

      <div className="pt-2 space-y-2">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-12 bg-[#1f6d62] hover:bg-[#174f46] text-white font-extrabold uppercase tracking-wide rounded-full shadow-lg transition-all"
          data-testid="dog-step-save"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
          ) : (
            <>Save & continue <ArrowRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
        <button
          type="button"
          onClick={() => (onSkip || onDone) && (onSkip ? onSkip() : onDone())}
          disabled={saving}
          className="w-full py-2.5 text-sm font-bold text-gray-500 hover:text-[#1f6d62] transition-colors"
          data-testid="dog-step-skip"
        >
          {skipLabel}
        </button>
      </div>
    </div>
  );
};
